import { kebabCase } from 'lodash';
import { createLogger } from '@owl-lib/logger';
import { deserialize, fetchWrapper } from '../fetch';
import type { ClaimFile, GetPresignedUploadResponse } from './interface';
const logger = createLogger(__filename);

export const FILE_CONTENT_TIMEOUT_LIMIT = 60_000;
const filesApiClient = {
  fetch: async (dossier_id: string): Promise<ClaimFile[]> => {
    const url = new URL(`/app/v1/dossiers/${dossier_id}/files`, API_BASE_URL);
    logger.trace('filesApiClient.files()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  fetchFile: async (data: {
    dossier_id: string;
    file_id: string;
  }): Promise<ClaimFile> => {
    const url = new URL(`/app/v1/files/${data.file_id}`, API_BASE_URL);
    logger.trace('filesApiClient.fetchFile()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  fetchFilePage: async (data: {
    dossierId: string;
    fileId: string;
    startPage: number;
    endPage: number;
  }): Promise<{
    pages: { url: string; page: number }[];
  }> => {
    const { dossierId, fileId, startPage, endPage } = data;
    const url = new URL(`/app/v1/files/${fileId}/pages/contents`, API_BASE_URL);
    url.searchParams.set('dossier_id', dossierId);
    url.searchParams.set('start_page', `${startPage}`);
    url.searchParams.set('end_page', `${endPage}`);
    logger.trace('filesApiClient.fetchFilePage()');

    const start = Date.now();
    const response = await fetchWrapper(
      url.href,
      { method: 'GET', credentials: 'include' },
      FILE_CONTENT_TIMEOUT_LIMIT
    );
    const duration = Date.now() - start;
    if (duration > 1500) {
      logger.error(
        `File loading duration exceeded: file: ${fileId} for dossier: ${dossierId} took ${duration}ms`
      );
    }

    return deserialize(response);
  },
  fetchFilesOCR: async ({
    dossierId,
    fileId,
    dataVersion,
  }: {
    dossierId: string;
    fileId: string;
    dataVersion: string;
  }): Promise<{ url: string }> => {
    const url = new URL(
      `/app/v1/files/${fileId}/run/${dataVersion}/ocr`,
      API_BASE_URL
    );
    url.searchParams.set('dossier_id', dossierId);
    logger.trace('filesApiClient.fetchFilesOCR()');
    const presignedUrl = await fetchWrapper(
      url.href,
      { method: 'GET', credentials: 'include' },
      FILE_CONTENT_TIMEOUT_LIMIT
    );
    const deserializedUrl = await deserialize(presignedUrl);
    const ocrData = await fetch(deserializedUrl.url);
    const ocrStrings = await ocrData.json();
    return ocrStrings;
  },
  fetchFileNotepads: async ({
    dossierId,
    fileId,
    dataVersion,
  }): Promise<{ [notepadKey: string]: string }> => {
    const url = new URL(
      `/app/v1/files/${fileId}/run/${dataVersion}/notepad`,
      API_BASE_URL
    );
    url.searchParams.set('dossier_id', dossierId);
    logger.trace('filesApiClient.fetchFileNotepads()');
    return deserialize(
      await fetchWrapper(
        url.href,
        { method: 'GET', credentials: 'include' },
        FILE_CONTENT_TIMEOUT_LIMIT
      )
    );
  },
  fetchFileSource: async (data: {
    dossierId: string;
    fileId: string;
  }): Promise<{ url: string }> => {
    const { dossierId, fileId } = data;
    const url = new URL(`/app/v1/files/${fileId}/contents`, API_BASE_URL);
    url.searchParams.set('dossier_id', dossierId);
    logger.trace('filesApiClient.fetchFileSource()');
    return deserialize(
      await fetchWrapper(
        url.href,
        { method: 'GET', credentials: 'include' },
        FILE_CONTENT_TIMEOUT_LIMIT
      )
    );
  },
  fetchAudioTranscript: async (data: {
    dossierId: string;
    fileId: string;
    dataVersion?: string;
  }): Promise<string> => {
    const { dossierId, fileId, dataVersion } = data;
    const url = new URL(`/app/v1/files/${fileId}/audio_contents`, API_BASE_URL);
    url.searchParams.set('dossier_id', dossierId);
    if (dataVersion) {
      url.searchParams.set('data_version', dataVersion);
    }
    logger.trace('filesApiClient.fetchAudioTranscript()');
    return deserialize(
      await fetchWrapper(
        url.href,
        { method: 'GET', credentials: 'include' },
        FILE_CONTENT_TIMEOUT_LIMIT
      )
    );
  },
  downloadFile: async (fileId: string): Promise<{ url: string }> => {
    const url = new URL(`/app/v1/files/${fileId}/contents`, API_BASE_URL);

    logger.trace('filesApiClient.downloadFile()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  deleteFile: async (fileId: string): Promise<void> => {
    const url = new URL(`/app/v1/files/${fileId}`, API_BASE_URL);
    logger.trace('filesApiClient.deleteFile()');
    return deserialize(
      await fetchWrapper(url.href, {
        method: 'DELETE',
        credentials: 'include',
      })
    );
  },
  uploadPresignedUrl: async (data: {
    fileName: string;
    contentType: string;
    uploadId: string;
  }): Promise<{
    url: string;
    contentType: string;
    metadata: {
      fileName: string;
      uploadId: string;
    };
  }> => {
    const { fileName, contentType, uploadId } = data;
    const url = new URL(`/app/v1/files/presigned`, API_BASE_URL);

    logger.trace('filesApiClient.uploadPresignedUrl()');
    return deserialize(
      await fetchWrapper(url.href, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify({
          file_name: fileName,
          content_type: contentType,
          upload_id: uploadId,
        }),
      })
    );
  },
  upload: async (data: GetPresignedUploadResponse) => {
    const { url: presignedUrl, contentType, file, metadata } = data;
    const metadataHeaders = Object.fromEntries(
      Object.entries(metadata).map(([k, v]) => [
        `x-amz-meta-${kebabCase(k)}`,
        v,
      ])
    );
    const url = new URL(presignedUrl);
    const paramKeys = Array.from(url.searchParams.keys()).map((k) =>
      k.toLowerCase()
    );

    if (
      !url.hostname.endsWith('.amazonaws.com') ||
      paramKeys.indexOf('x-amz-signature') === -1
    ) {
      logger.error(data, 'Not a valid Presigned S3 URL');
      throw new Error('Not a valid Presigned S3 URL');
    }

    logger.trace('upload s3 file');
    const response = await fetchWrapper(
      url.href,
      {
        method: 'PUT',
        headers: { ...metadataHeaders, 'content-type': contentType },
        body: await file.arrayBuffer(),
      },
      -1
    );
    logger.debug({ response }, `PUT ${url.href}`);
    return deserialize(response);
  },
};

export default filesApiClient;
